<template lang="pug">
.scan-products
    .scan-products__search
        el-input.scan-products__search-input(
            ref="searchInput"
            v-model="search"
            :placeholder="$t('Produkt suchen…')"
            clearable
            prefix-icon="el-icon-search"
            enterkeyhint="search"
            @input="onSearchInput"
            @clear="onSearchClear"
            @keyup.enter.native="onSearchEnter"
        )
        el-button.scan-products__scan(
            type="primary"
            icon="el-icon-fa-barcode fas"
            :loading="lookingUp"
            :title="$t('Barcode scannen')"
            @click="scannerOpen = true"
        )

    barcode-scanner(
        v-if="scannerOpen"
        @detected="onBarcodeDetected"
        @close="scannerOpen = false"
    )

    //- Hinweis + Zurücksetzen, wenn eine Scan-Entscheidung gemerkt ist
    .scan-products__pref(v-if="scanPref")
        span.scan-products__pref-text
            | {{ $t('Scan öffnet immer') }}: {{ scanPref === 'article' ? $t('Artikel') : $t('Produkt') }}
        el-button(type="text" size="mini" @click="resetScanPref") {{ $t('Zurücksetzen') }}

    .page-loading(v-if="loading && !products.length")
        el-skeleton(:rows="4" animated)

    el-empty(
        v-else-if="!products.length"
        :description="$t('Keine Produkte gefunden')"
    )

    template(v-else)
        el-card.product-card(
            v-for="product in products"
            :key="product.id"
            shadow="hover"
            @click.native="goToProduct(product)"
        )
            .product-card-inner
                .product-thumb
                    img(v-if="thumbUrl(product)" :src="thumbUrl(product)" :alt="product.name")
                    i.el-icon-picture-outline(v-else)
                .product-info
                    .product-name {{ product.name }}
                    .product-nr(v-if="product.nr") {{ product.nr }}
                    .product-images-badge(:class="{ empty: !imageCount(product) }")
                        i.el-icon-fa-camera.fas
                        |  {{ imageCount(product) }} {{ imageCount(product) === 1 ? $t('Bild') : $t('Bilder') }}
                i.el-icon-arrow-right.arrow

        .load-more(v-if="hasMore")
            el-button(
                :loading="loadingMore"
                @click="loadMore"
            ) {{ $t('Mehr laden') }}
</template>

<i18n>
{
  "de": {
    "Produkt suchen…": "Produkt suchen…",
    "Keine Produkte gefunden": "Keine Produkte gefunden",
    "Bild": "Bild",
    "Bilder": "Bilder",
    "Mehr laden": "Mehr laden",
    "Barcode scannen": "Barcode scannen",
    "Kein Treffer": "Kein Treffer",
    "Kein Treffer für": "Kein Treffer für",
    "Suche fehlgeschlagen": "Suche fehlgeschlagen",
    "Treffer": "Treffer",
    "Möchtest du den Artikel oder das Produkt öffnen?": "Möchtest du den Artikel oder das Produkt öffnen?",
    "Zum Artikel": "Zum Artikel",
    "Zum Produkt": "Zum Produkt",
    "Entscheidung merken": "Entscheidung merken",
    "Scan öffnet immer": "Scan öffnet immer",
    "Artikel": "Artikel",
    "Produkt": "Produkt",
    "Zurücksetzen": "Zurücksetzen",
    "Scan-Auswahl zurückgesetzt": "Scan-Auswahl zurückgesetzt"
  }
}
</i18n>

<script>
import _ from 'lodash'
import { useSetupStore } from '@/stores'
import AuthApi from '@/store/api/system/auth'
import ProductApi from '@/extensions/shop/store/api/product'
import ArticleApi from '@/extensions/shop/store/api/article'
import BarcodeScanner from './BarcodeScanner.vue'

const PER_PAGE = 25

// Modul-übergreifend gemerkter Suchbegriff – überlebt das Verlassen/Zurück der Liste
// (die Komponente wird beim Navigieren zerstört). Auf SPA-Sitzung beschränkt.
let lastSearch = ''

export default {
    name: 'ScanProducts',
    components: { BarcodeScanner },
    data() {
        return {
            products: [],
            search: lastSearch, // zuletzt gemerkten Suchbegriff wiederherstellen
            currentPage: 1,
            lastPage: 1,
            loading: true,
            loadingMore: false,
            scannerOpen: false,
            lookingUp: false,
            scanPref: null, // gemerktes Scan-Ziel ('article' | 'product' | null)
        }
    },
    computed: {
        cmsId() { return useSetupStore().activeCms?.id },
        shopId() {
            const id = useSetupStore().activeCms?.shop?.id
            return id != null ? parseInt(id) : null
        },
        hasMore() { return this.currentPage < this.lastPage },
        storageRoute() {
            return _.trim(
                `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`,
                '/'
            )
        },
    },
    created() {
        // Tippen verzögert abfragen, damit nicht jede Eingabe eine Query auslöst.
        this.debouncedSearch = _.debounce(() => this.load(true), 350)
    },
    mounted() {
        this.load(true)
        this.scanPref = this.rememberedScanTarget()
        // Suchfeld beim Öffnen direkt fokussieren
        this.$nextTick(() => this.$refs.searchInput && this.$refs.searchInput.focus())
    },
    methods: {
        onSearchInput(value) {
            if (typeof value === 'string') this.search = value
            lastSearch = this.search // für Rückkehr zur Liste merken
            this.debouncedSearch()
        },
        // Nach dem Leeren (x) direkt wieder ins Suchfeld fokussieren.
        onSearchClear() {
            this.onSearchInput('')
            this.$nextTick(() => this.$refs.searchInput && this.$refs.searchInput.focus())
        },
        // Such-Taste (Lupe) der Tastatur → sofort suchen und Tastatur schließen.
        onSearchEnter() {
            if (this.debouncedSearch && this.debouncedSearch.cancel) this.debouncedSearch.cancel()
            this.load(true)
            if (this.$refs.searchInput) this.$refs.searchInput.blur()
        },
        async load(reset = false) {
            if (reset) {
                this.currentPage = 1
                this.loading = true
            } else {
                this.loadingMore = true
            }

            const term = this.search.trim()
            try {
                if (term) {
                    await this.loadSearch(term)
                } else {
                    await this.loadPaginated(reset)
                }
            } catch {
                if (reset) this.products = []
            } finally {
                this.loading = false
                this.loadingMore = false
            }
        },
        // Ohne Suchbegriff: normale Paginierung (mit „Mehr laden").
        async loadPaginated(reset) {
            const result = await this.fetchProducts([], this.currentPage)
            const items = result?.items || []
            this.products = reset ? items : [...this.products, ...items]
            this.currentPage = result?.pageInfo?.currentPage || this.currentPage
            this.lastPage = result?.pageInfo?.lastPage || this.currentPage
            this.$root.$emit('scan:products', result?.pageInfo?.total ?? this.products.length)
        },
        // Mit Suchbegriff: nach Name ODER Produkt-Nr suchen (zwei Abfragen, gemerged) und
        // client-seitig gegenprüfen, damit auch bei serverseitig ignoriertem Filter nur
        // echte Treffer erscheinen. Keine Paginierung bei aktiver Suche.
        async loadSearch(term) {
            const mk = key => [{ key, match: '', like: term, from: '', to: '', in: [] }]
            const [byName, byNr] = await Promise.all([
                this.fetchProducts(mk('name'), 1).then(r => r?.items || []).catch(() => []),
                this.fetchProducts(mk('nr'), 1).then(r => r?.items || []).catch(() => []),
            ])
            const t = term.toLowerCase()
            const seen = new Set()
            const merged = []
            ;[...byName, ...byNr].forEach(p => {
                if (!p || seen.has(p.id)) return
                const matches = String(p.name || '').toLowerCase().includes(t)
                    || String(p.nr || '').toLowerCase().includes(t)
                if (matches) { seen.add(p.id); merged.push(p) }
            })
            this.products = merged
            this.currentPage = 1
            this.lastPage = 1
            this.$root.$emit('scan:products', merged.length)
        },
        fetchProducts(filters, page) {
            return ProductApi.fetchProductsPaginated(
                {
                    scope: 'ex',
                    type: 'shop_product',
                    pageInfo: { currentPage: page, perPage: PER_PAGE },
                    variables: { order: 'name', direction: 'asc' },
                    filters,
                    includeConfig: false,
                },
                false,
                true // extended → liefert media für Thumbnails/Zähler
            )
        },
        async loadMore() {
            if (!this.hasMore) return
            this.currentPage += 1
            await this.load(false)
        },
        productImages(product) {
            return (product.media || []).filter(
                m => m.type === 'image' && !_.get(m, 'settings.show.disabled')
            )
        },
        imageCount(product) {
            return this.productImages(product).length
        },
        // Das in der Liste anzuzeigende Bild wählen: Hauptbild (highlight) hat Vorrang,
        // dann Listenbild (list), sonst das erste nach Position. So erscheint ein als
        // Hauptbild markiertes (z. B. KI-)Bild auch dann, wenn es nicht an Position 0 liegt.
        primaryImage(product) {
            const images = this.productImages(product)
            if (!images.length) return null
            const sorted = _.sortBy(images, m => _.get(m, 'position', 0))
            return sorted.find(m => _.get(m, 'settings.show.highlight'))
                || sorted.find(m => _.get(m, 'settings.show.list'))
                || sorted[0]
        },
        // Thumbnail-URL wählen. KI-bearbeitete/generierte Bilder tragen ihre aktiven Pixel in
        // processedFile bzw. activeVariant (nicht in source) — genau wie Detail.vue mediaUrl.
        // Diese haben Vorrang, damit ein als Hauptbild gewähltes KI-Bild auch hier erscheint.
        // Sonst aus der Source eine verkleinerte Thumbnail-URL bauen (Formel wie ProductMedium.src).
        thumbUrl(product) {
            const media = this.primaryImage(product)
            const processed = _.get(media, 'image.processedFile.url')
                || _.get(media, 'image.activeVariant.file.url')
            if (processed) return processed
            const source = _.get(media, 'image.source')
            const imageId = _.get(media, 'image.id')
            if (source && imageId) {
                const token = AuthApi.getToken()
                return `${this.storageRoute}/images/${imageId}/${source.name}?hash=${source.hash}&token=${token}&fit&h=160`
            }
            return _.get(media, 'image.source.url') || null
        },
        goToProduct(product) {
            this.$router.push({ name: 'scan-product', params: { id: product.id } })
        },
        // Barcode gescannt → Produkt-Nr / SKU / GTIN finden. Produkt-Nr → direkt zum Produkt;
        // Artikel-Treffer (SKU/GTIN) → nachfragen, ob zum Artikel oder zum Produkt.
        async onBarcodeDetected(code) {
            this.scannerOpen = false
            this.lookingUp = true
            let target = null
            try {
                target = await this.findTargetByCode(code)
            } catch {
                this.lookingUp = false
                this.$notify.error({
                    title: this.$t('Suche fehlgeschlagen'),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000,
                })
                return
            }
            this.lookingUp = false

            if (!target) {
                this.$notify.warning({
                    title: this.$t('Kein Treffer'),
                    message: `${this.$t('Kein Treffer für')} ${code}`,
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000,
                })
                return
            }

            if (target.name === 'scan-article') {
                // Gemerkte Entscheidung anwenden, sonst nachfragen.
                const pref = this.rememberedScanTarget()
                if (pref === 'article') {
                    this.goToArticleTarget(target)
                } else if (pref === 'product') {
                    this.goToProductTarget(target)
                } else {
                    this.askArticleOrProduct(target)
                }
            } else {
                this.$router.push({ name: target.name, params: { id: target.id } })
            }
        },
        goToArticleTarget(target) {
            this.$router.push({ name: 'scan-article', params: { id: target.id } })
        },
        goToProductTarget(target) {
            if (target.productId) {
                this.$router.push({ name: 'scan-product', params: { id: target.productId } })
            } else {
                // Kein Produkt bekannt → ersatzweise zum Artikel
                this.goToArticleTarget(target)
            }
        },
        rememberedScanTarget() {
            try { return localStorage.getItem('scanBarcodeTarget') } catch { return null }
        },
        setRememberedScanTarget(value) {
            try { localStorage.setItem('scanBarcodeTarget', value) } catch { /* ignore */ }
            this.scanPref = value
        },
        // Gemerkte Scan-Entscheidung zurücksetzen → nächster Scan fragt wieder.
        resetScanPref() {
            try { localStorage.removeItem('scanBarcodeTarget') } catch { /* ignore */ }
            this.scanPref = null
            this.$notify.success({
                title: this.$t('Scan-Auswahl zurückgesetzt'),
                position: 'bottom-left',
                customClass: 'dark',
                duration: 4000,
            })
        },
        // Artikel-Treffer: Auswahl Artikel vs. Produkt, optional merken.
        askArticleOrProduct(target) {
            const h = this.$createElement
            const remember = { value: false }
            this.$msgbox({
                title: this.$t('Treffer'),
                message: h('div', [
                    h('p', { style: 'margin: 0 0 12px' }, this.$t('Möchtest du den Artikel oder das Produkt öffnen?')),
                    // Native Checkbox (toggelt selbst, liefert checked zuverlässig) –
                    // el-checkbox ist ohne value-Bindung im MessageBox unzuverlässig.
                    h('label', { style: 'display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px' }, [
                        h('input', {
                            attrs: { type: 'checkbox' },
                            on: { change: e => { remember.value = e.target.checked } },
                        }),
                        h('span', this.$t('Entscheidung merken')),
                    ]),
                ]),
                showCancelButton: true,
                confirmButtonText: this.$t('Zum Artikel'),
                cancelButtonText: this.$t('Zum Produkt'),
                distinguishCancelAndClose: true,
                customClass: 'dark',
            }).then(() => {
                if (remember.value) this.setRememberedScanTarget('article')
                this.goToArticleTarget(target)
            }).catch(action => {
                if (action === 'cancel') {
                    if (remember.value) this.setRememberedScanTarget('product')
                    this.goToProductTarget(target)
                }
                // action === 'close' → nichts tun
            })
        },
        // Liefert das Sprungziel zum Barcode oder null:
        // Produkt-Nr → Produkt-Ansicht, SKU/GTIN → Artikel-Ansicht (jeweils exakt verifiziert).
        async findTargetByCode(code) {
            const term = String(code).trim()
            if (!term) return null

            // 1) Produkt-Nr (Produkt-Ebene)
            try {
                const res = await ProductApi.fetchProductsPaginated({
                    scope: 'ex',
                    type: 'shop_product',
                    pageInfo: { currentPage: 1, perPage: 1 },
                    filters: [{ key: 'nr', match: term, like: '', from: '', to: '', in: [] }],
                    includeConfig: false,
                }, false, false)
                const item = _.get(res, 'items.0')
                if (item && item.id && String(item.nr || '') === term) {
                    return { name: 'scan-product', id: item.id }
                }
            } catch { /* weiter mit SKU */ }

            // 2) SKU – direkter Artikel-Lookup (CO_Article akzeptiert sku). shop ist nötig,
            //    sonst liefert die Query nichts (wie im Kassensystem, posCart.findArticle).
            try {
                const variables = { sku: term }
                if (this.shopId) variables.shop = this.shopId
                const article = await ArticleApi.findArticle(variables, 'no-cache')
                if (article && article.id) {
                    return { name: 'scan-article', id: article.id, productId: _.get(article, 'product.id') }
                }
            } catch { /* weiter mit GTIN */ }

            // 3) GTIN – die paginierte Query liefert nur die Artikel-ID (kein gtin/product in
            //    den items); daher vollen Artikel nachladen und GTIN exakt verifizieren.
            try {
                const res = await ArticleApi.fetchArticlesPaginated({
                    scope: 'ex',
                    type: 'shop_article',
                    pageInfo: { currentPage: 1, perPage: 1 },
                    filters: [{ key: 'gtin', match: term, like: '', from: '', to: '', in: [] }],
                })
                const articleId = _.get(res, 'items.0.id')
                if (articleId) {
                    const variables = { id: articleId }
                    if (this.shopId) variables.shop = this.shopId
                    const full = await ArticleApi.findArticle(variables, 'no-cache')
                    if (full && String(full.gtin || '') === term) {
                        return { name: 'scan-article', id: full.id, productId: _.get(full, 'product.id') }
                    }
                }
            } catch { /* nicht gefunden */ }

            return null
        },
    },
}
</script>

<style lang="sass" scoped>
.scan-products
    flex: 1
    min-height: 0
    overflow-y: auto
    width: 100%
    padding: 16px
    max-width: 480px
    margin: 0 auto

.scan-products__pref
    display: flex
    align-items: center
    justify-content: space-between
    gap: 8px
    margin: -6px 0 14px
    padding: 6px 10px
    border-radius: 8px
    background: #161616
    border: 1px solid #2a2a2a

    .scan-products__pref-text
        font-size: 12px
        color: #888

    ::v-deep .el-button--text
        color: $--color-primary
        padding: 0

.scan-products__search
    margin-bottom: 16px
    display: flex
    gap: 8px

    .scan-products__search-input
        flex: 1
        min-width: 0

    ::v-deep
        .el-input__inner
            background: #1a1a1a
            border-color: #2a2a2a
            color: #d0d0d0
        .el-input__icon
            color: #666

    .scan-products__scan
        flex-shrink: 0
        padding: 0 14px
        font-size: 16px

.page-loading
    padding: 16px
    ::v-deep
        .el-skeleton__item
            background: #1a1a1a

.product-card
    margin-bottom: 12px
    border-radius: 10px
    cursor: pointer
    -webkit-tap-highlight-color: transparent

    ::v-deep
        &.el-card
            background: #1a1a1a
            border-color: #2a2a2a
            &:hover
                border-color: $--color-primary
        .el-card__body
            padding: 0

    .product-card-inner
        display: flex
        align-items: center
        padding: 12px
        gap: 12px

    .product-thumb
        width: 56px
        height: 56px
        border-radius: 8px
        flex-shrink: 0
        background: #0f0f0f
        border: 1px solid #2a2a2a
        display: flex
        align-items: center
        justify-content: center
        overflow: hidden

        img
            width: 100%
            height: 100%
            object-fit: cover

        i
            font-size: 22px
            color: #444

    .product-info
        flex: 1
        min-width: 0

    .product-name
        font-size: 15px
        font-weight: 600
        color: #d0d0d0
        margin-bottom: 4px
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis

    .product-nr
        font-size: 12px
        color: #777
        margin-bottom: 6px

    .product-images-badge
        font-size: 12px
        color: $--color-primary
        i
            margin-right: 4px
        &.empty
            color: #b9803a

    .arrow
        color: #333
        font-size: 14px
        flex-shrink: 0

.load-more
    text-align: center
    padding: 8px 0 24px

    ::v-deep .el-button
        background: #1a1a1a
        border-color: #2a2a2a
        color: #d0d0d0

::v-deep .el-empty
    &__description p
        color: #666
</style>

<!-- Der Artikel/Produkt-Auswahldialog ($msgbox) wird an document.body gehängt und hat
     eine feste Breite (~420px), die auf schmalen Screens überläuft. Unscoped + ≤768px,
     damit die Box mobil schmaler und zentriert ist. -->
<style lang="sass">
@media (max-width: 768px)
    .el-message-box
        width: 90vw !important
        max-width: 360px !important
</style>
